import { FunctionComponent, useCallback, useEffect, useMemo, useState, Dispatch, SetStateAction } from 'react';
import { GeoJsonProperties, Geometry, Feature } from 'geojson';
import { useNavigate } from 'react-router-dom';
import Slider from '@mui/material/Slider';
import CircularProgress from '@mui/material/CircularProgress';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import Map from '../../components/maps/Map';
import SelectInput, { SelectDataType } from '../../components/SelectInput';
import CheckBox from '../../components/CheckBox';
import { SimpleObject } from '../../types';
import { getUncatchEndpointData, performUncatchedRequest } from '../../utils/request';
import {
  clampFpMagnitud,
  fpMagnitudSliderBounds,
  type TipoGdFp
} from '../../utils/factorPotenciaNormativo';
import '../../css/PrivateEstudioConexion.css';

type NivelTension = 'mt' | 'bt';

const PrivateEstudioConexionPage: FunctionComponent = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // General
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');

  // Alimentador cascade
  const [empresas, setEmpresas] = useState<SelectDataType[]>([]);
  const [selectedEmpresa, setSelectedEmpresa] = useState('');
  const [subEstaciones, setSubEstaciones] = useState<SelectDataType[]>([]);
  const [selectedSubEstacion, setSelectedSubEstacion] = useState('');
  const [alimentadores, setAlimentadores] = useState<SelectDataType[]>([]);
  const [allAlimentadores, setAllAlimentadores] = useState<SelectDataType[]>([]);
  const [selectedAlimentador, setSelectedAlimentador] = useState('');

  // Map
  const [mapState, setMapState] = useState({
    mapPointFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    mapLineFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    mapPolygonFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    electricFeeders: {} as SimpleObject,
    mapCenter: {} as { lat: number; lng: number }
  });

  // Nodo
  const [nivelTension, setNivelTension] = useState<NivelTension>('mt');
  const [selectedPosteId, setSelectedPosteId] = useState<number | null>(null);
  const [nodoResuelto, setNodoResuelto] = useState<SimpleObject | null>(null);
  const [resolviendoNodo, setResolviendoNodo] = useState(false);

  // Proyecto
  const [tipoGd, setTipoGd] = useState<TipoGdFp>('PMGD');
  const [potenciaKw, setPotenciaKw] = useState<string>('');
  const [factorPotencia, setFactorPotencia] = useState<number>(1.0);
  const [fpNaturaleza, setFpNaturaleza] = useState<'inductivo' | 'capacitivo'>('capacitivo');
  const [tipoInversor, setTipoInversor] = useState('inversor');

  // Batería
  const [bateriaIncluida, setBateriaIncluida] = useState(false);
  const [batPotencia, setBatPotencia] = useState('');
  const [batEnergia, setBatEnergia] = useState('');
  const [batBloqueCarga, setBatBloqueCarga] = useState('22:00-06:00');
  const [batBloqueDescarga, setBatBloqueDescarga] = useState('10:00-16:00');

  const fpBounds = useMemo(
    () => fpMagnitudSliderBounds(tipoGd, fpNaturaleza),
    [tipoGd, fpNaturaleza]
  );

  useEffect(() => {
    setFactorPotencia((prev) => clampFpMagnitud(prev, tipoGd, fpNaturaleza));
  }, [tipoGd, fpNaturaleza]);

  const getData = useCallback(
    async (
      endpoint: string,
      setState: Dispatch<SetStateAction<SelectDataType[]>>,
      valueKey: string,
      nameKey: string,
      query?: any
    ) => {
      try {
        const res = await getUncatchEndpointData({ endpoint, query });
        if (res && res.length > 0) {
          setState(res.map((row: SimpleObject) => ({ value: row[valueKey], name: row[nameKey] })));
        }
      } catch (error) {
        console.error(error);
        setState([]);
      }
    },
    []
  );

  useEffect(() => {
    getData('alimentador', setAllAlimentadores, 'alimentadorId', 'alimentadorNombre');
    getData('empresa', setEmpresas, 'empresaId', 'empresaNombre');
  }, [getData]);

  useEffect(() => {
    if (selectedEmpresa) {
      getData('sseePoder', setSubEstaciones, 'sseePoderId', 'sseePoderNombre', {
        empresaId: selectedEmpresa
      });
      setSelectedSubEstacion('');
      setSelectedAlimentador('');
    } else {
      setSubEstaciones([]);
    }
  }, [getData, selectedEmpresa]);

  useEffect(() => {
    if (selectedSubEstacion) {
      getData('alimentador', setAlimentadores, 'alimentadorId', 'alimentadorNombre', {
        sseePoderId: selectedSubEstacion
      });
      setSelectedAlimentador('');
    } else {
      setAlimentadores([]);
    }
  }, [getData, selectedSubEstacion]);

  const fetchMapPoints = async (alimentadorId: string) => {
    try {
      setLoading(true);
      const res = await getUncatchEndpointData({
        endpoint: 'mapElements/geoJSON/all',
        query: { alimentadorId }
      });
      const { pointFeatures, lineFeatures, polygonFeatures, alimentadores } = res;
      if ((!pointFeatures || pointFeatures.length === 0) && (!lineFeatures || lineFeatures.length === 0)) {
        Swal.fire({ icon: 'warning', title: 'No se encontraron elementos para este alimentador' });
        setLoading(false);
        return;
      }
      const center = { lat: 0, lng: 0 };
      if (pointFeatures?.length > 1) {
        center.lng = pointFeatures[1].geometry.coordinates[0];
        center.lat = pointFeatures[1].geometry.coordinates[1];
      } else if (lineFeatures?.length > 1) {
        center.lng = lineFeatures[1].geometry.coordinates[0][0];
        center.lat = lineFeatures[1].geometry.coordinates[0][1];
      }
      setMapState({
        mapPointFeatures: pointFeatures || [],
        mapLineFeatures: lineFeatures || [],
        mapPolygonFeatures: polygonFeatures || [],
        electricFeeders: alimentadores || {},
        mapCenter: center
      });
      setSelectedPosteId(null);
      setNodoResuelto(null);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const handleAlimentadorChange = (alimentadorId: string) => {
    setSelectedAlimentador(alimentadorId);
    if (alimentadorId) {
      fetchMapPoints(alimentadorId);
    }
  };

  const handleMapClick = (data: SimpleObject) => {
    if (data?.artefacto === 'Poste') {
      const rawId = data?.id ?? data?.poste_id ?? data?.descripcionArtefacto?.poste_id;
      const numId = Number(rawId);
      if (numId && !Number.isNaN(numId)) {
        setSelectedPosteId(numId);
      }
    }
  };

  const resolveNodo = useCallback(async () => {
    if (!selectedPosteId || !selectedAlimentador) return;
    setResolviendoNodo(true);
    try {
      const res = await getUncatchEndpointData({
        endpoint: 'estudios-conexion/nodo-cercano',
        query: {
          posteId: selectedPosteId,
          alimentadorId: selectedAlimentador,
          nivelTension
        }
      });
      setNodoResuelto(res);
    } catch (error) {
      console.error(error);
      setNodoResuelto(null);
    }
    setResolviendoNodo(false);
  }, [selectedPosteId, selectedAlimentador, nivelTension]);

  useEffect(() => {
    resolveNodo();
  }, [resolveNodo]);

  const MT_ACTIVE = '#e06c00';
  const MT_INACTIVE = '#f0c8a0';
  const BT_ACTIVE = '#0072b2';
  const BT_INACTIVE = '#a0cfe0';
  const UNKNOWN_COLOR = '#c8c8c8';

  const styledLineFeatures = useMemo(() => {
    if (!mapState.mapLineFeatures.length) return [];
    return mapState.mapLineFeatures.map((feature) => {
      const tensionKv = feature.properties?.descripcionArtefacto?.tension_kv;
      if (tensionKv == null) {
        return { ...feature, properties: { ...feature.properties, color: UNKNOWN_COLOR, lineWidth: 1.5 } };
      }
      const isMt = tensionKv > 1;
      const selected = nivelTension === 'mt' ? isMt : !isMt;
      const color = isMt
        ? (selected ? MT_ACTIVE : MT_INACTIVE)
        : (selected ? BT_ACTIVE : BT_INACTIVE);
      return {
        ...feature,
        properties: { ...feature.properties, color, lineWidth: selected ? 4 : 2 }
      };
    });
  }, [mapState.mapLineFeatures, nivelTension]);

  const styledPointFeatures = useMemo(() => {
    if (!mapState.mapPointFeatures.length) return [];
    return mapState.mapPointFeatures.map((feature) => {
      const featureId = Number(feature.properties?.id);
      const isSelected = selectedPosteId != null && featureId === selectedPosteId;
      if (!isSelected) return feature;
      return {
        ...feature,
        properties: { ...feature.properties, highlighted: true }
      };
    });
  }, [mapState.mapPointFeatures, selectedPosteId]);

  const handleSubmit = async () => {
    if (!nombre.trim()) {
      Swal.fire({ icon: 'warning', title: 'Ingrese un nombre para el estudio' });
      return;
    }
    if (!selectedAlimentador) {
      Swal.fire({ icon: 'warning', title: 'Seleccione un alimentador' });
      return;
    }
    if (!nodoResuelto) {
      Swal.fire({ icon: 'warning', title: 'Seleccione un poste en el mapa para resolver el nodo de conexión' });
      return;
    }
    const pot = Number(potenciaKw);
    if (!pot || pot <= 0) {
      Swal.fire({ icon: 'warning', title: 'Ingrese una potencia válida (kW)' });
      return;
    }

    setSubmitting(true);
    try {
      const body: SimpleObject = {
        nombre: nombre.trim(),
        descripcion: descripcion.trim() || undefined,
        alimentadorId: Number(selectedAlimentador),
        posteId: selectedPosteId,
        nodoConexion: nodoResuelto.nodoId?.toString(),
        latitud: nodoResuelto.y,
        longitud: nodoResuelto.x,
        tipoGd,
        potenciaKw: pot,
        factorPotencia,
        factorPotenciaNaturaleza: fpNaturaleza,
        tipoInversor,
        bateriaIncluida,
        bateria: bateriaIncluida
          ? {
              potencia_kw: Number(batPotencia),
              energia_kwh: Number(batEnergia),
              bloque_carga: batBloqueCarga,
              bloque_descarga: batBloqueDescarga
            }
          : undefined
      };

      const addRes = await toast.promise(
        performUncatchedRequest({ endpoint: 'estudios-conexion/add', method: 'post', data: body }),
        {
          pending: 'Creando estudio...',
          success: 'Estudio creado exitosamente',
          error: 'Error al crear el estudio'
        }
      );

      const estudioId = addRes?.data?.id;
      if (estudioId) {
        await toast.promise(
          performUncatchedRequest({ endpoint: `estudios-conexion/${estudioId}/run`, method: 'post' }),
          {
            pending: 'Ejecutando estudio base...',
            success: 'Estudio en ejecución',
            error: 'Error al ejecutar el estudio'
          }
        );
        navigate(`/private/estudio-conexion/${estudioId}/resultado`);
      }
    } catch (error: any) {
      console.error(error);
      Swal.fire({ icon: 'error', title: 'Error', text: error?.response?.data?.details || error.message });
    }
    setSubmitting(false);
  };

  return (
    <div className='ecg-page-container vdr-ecg-form'>
      <div className='ecg-title-container vdr-page-head'>
        <div className='vdr-ecg-heading vdr-page-head__titles'>
          <span className='vdr-eyebrow vdr-eyebrow--bar'>Estudio de conexión GD · Nuevo estudio</span>
          <h2 className='ecg-title vdr-page-head__title'>Nuevo Estudio de Conexión GD</h2>
        </div>
        <div className='vdr-ecg-actions vdr-page-head__actions'>
          <button className='secondary-button-style vdr-ecg-back' onClick={() => navigate('/private/estudios')}>
            Volver a Mis estudios
          </button>
        </div>
      </div>

      {/* General */}
      <div className='ecg-section'>
        <h3 className='ecg-section-title'>General</h3>
        <div className='ecg-form-row'>
          <div className='ecg-form-field ecg-form-field-30'>
            <label className='ecg-label'>Nombre del Estudio</label>
            <input
              type='text'
              className='ecg-input'
              placeholder='Nombre del estudio'
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
          </div>
          <div className='ecg-form-field ecg-form-field-70'>
            <label className='ecg-label'>Descripción</label>
            <textarea
              className='ecg-textarea'
              placeholder='Descripción (opcional)'
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Alimentador */}
      <div className='ecg-section'>
        <h3 className='ecg-section-title'>Alimentador</h3>
        <div className='ecg-form-row'>
          <div className='ecg-form-field'>
            <label className='ecg-label'>Buscar por nombre</label>
            <SelectInput
              label='Alimentador'
              name='alim-name'
              id='alim-name'
              value={selectedAlimentador}
              onChange={(e) => handleAlimentadorChange(e.target.value)}
              options={allAlimentadores}
            />
          </div>
        </div>
        <div className='ecg-form-row'>
          <div className='ecg-form-field'>
            <label className='ecg-label'>Empresa</label>
            <SelectInput
              label='Seleccione empresa'
              name='empresa'
              id='empresa'
              value={selectedEmpresa}
              onChange={(e) => setSelectedEmpresa(e.target.value)}
              options={empresas}
            />
          </div>
          <div className='ecg-form-field'>
            <label className='ecg-label'>Subestación</label>
            <SelectInput
              label='Seleccione subestación'
              name='subestacion'
              id='subestacion'
              value={selectedSubEstacion}
              onChange={(e) => setSelectedSubEstacion(e.target.value)}
              options={subEstaciones}
            />
          </div>
          <div className='ecg-form-field'>
            <label className='ecg-label'>Alimentador</label>
            <SelectInput
              label='Seleccione alimentador'
              name='alimentador'
              id='alimentador'
              value={selectedAlimentador}
              onChange={(e) => handleAlimentadorChange(e.target.value)}
              options={alimentadores}
            />
          </div>
        </div>
      </div>

      {/* Punto de conexión */}
      <div className='ecg-section'>
        <h3 className='ecg-section-title'>Punto de Conexión</h3>
        <p className='vdr-ecg-lead'>
          Haga clic sobre un <strong>poste</strong> en el mapa para seleccionar el punto de conexión.
        </p>
        <div className='ecg-form-row'>
          <div className='ecg-form-field' style={{ maxWidth: 420 }}>
            <label className='ecg-label'>Nivel de tensión</label>
            <div className='ecg-radio-group'>
              <label className='ecg-radio-option'>
                <input
                  type='radio'
                  name='nivelTension'
                  value='mt'
                  checked={nivelTension === 'mt'}
                  onChange={() => setNivelTension('mt')}
                />
                Media Tensión (MT)
              </label>
              <label className='ecg-radio-option'>
                <input
                  type='radio'
                  name='nivelTension'
                  value='bt'
                  checked={nivelTension === 'bt'}
                  onChange={() => setNivelTension('bt')}
                />
                Baja Tensión (BT)
              </label>
            </div>
            {selectedAlimentador && (
              <div className='ecg-tension-legend'>
                <span className='ecg-tension-legend-item'>
                  <span
                    className='ecg-tension-swatch'
                    style={{
                      backgroundColor: nivelTension === 'mt' ? MT_ACTIVE : MT_INACTIVE,
                      height: nivelTension === 'mt' ? '5px' : '3px'
                    }}
                  />
                  MT {nivelTension === 'mt' && '(seleccionado)'}
                </span>
                <span className='ecg-tension-legend-item'>
                  <span
                    className='ecg-tension-swatch'
                    style={{
                      backgroundColor: nivelTension === 'bt' ? BT_ACTIVE : BT_INACTIVE,
                      height: nivelTension === 'bt' ? '5px' : '3px'
                    }}
                  />
                  BT {nivelTension === 'bt' && '(seleccionado)'}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className='ecg-map-container'>
          <Map
            mapPointFeatures={styledPointFeatures}
            mapLineFeatures={styledLineFeatures}
            mapPolygonFeatures={mapState.mapPolygonFeatures}
            electricFeeders={mapState.electricFeeders}
            mapCenter={mapState.mapCenter}
            onArtifactClick={handleMapClick}
          />
          {loading && (
            <div className='ecg-map-loader'>
              <CircularProgress />
            </div>
          )}
        </div>
        {selectedAlimentador && !selectedPosteId && (
          <div className='ecg-nodo-info ecg-nodo-pending'>
            Seleccione un poste en el mapa para definir el punto de conexión.
          </div>
        )}
        {selectedPosteId && resolviendoNodo && (
          <div className='ecg-nodo-info ecg-nodo-pending'>
            Resolviendo nodo para el poste seleccionado...
          </div>
        )}
        {selectedPosteId && !resolviendoNodo && !nodoResuelto && (
          <div className='ecg-nodo-info ecg-nodo-pending'>
            No se encontró un nodo asociado a este poste con la tensión seleccionada.
          </div>
        )}
      </div>

      {/* Datos del Proyecto */}
      <div className='ecg-section'>
        <h3 className='ecg-section-title'>Datos del Proyecto</h3>

        {/* Punto de Conexión — readonly, se llena al hacer clic en un poste */}
        <div className='ecg-subsection'>
          <h4 className='ecg-subsection-title'>Punto de Conexión</h4>
          {!selectedPosteId ? (
            <p className='ecg-hint'>Se completará automáticamente al seleccionar un poste en el mapa.</p>
          ) : resolviendoNodo ? (
            <p className='ecg-hint'>Resolviendo nodo de conexión...</p>
          ) : !nodoResuelto ? (
            <p className='ecg-hint ecg-hint-warn'>No se encontró un nodo para este poste con la tensión seleccionada. Pruebe otro poste o cambie el nivel de tensión.</p>
          ) : (
            <div className='ecg-form-row'>
              <div className='ecg-form-field' style={{ maxWidth: 160 }}>
                <label className='ecg-label'>Poste ID</label>
                <input type='text' className='ecg-input ecg-input-readonly' value={selectedPosteId} readOnly />
              </div>
              <div className='ecg-form-field' style={{ maxWidth: 200 }}>
                <label className='ecg-label'>Nodo de Conexión</label>
                <input
                  type='text'
                  className='ecg-input ecg-input-readonly'
                  value={`${nodoResuelto.nodoIdOrigen || nodoResuelto.nodoId}${nodoResuelto.metodo === 'cercania' ? ' (cercanía)' : ''}`}
                  readOnly
                />
              </div>
              <div className='ecg-form-field' style={{ maxWidth: 160 }}>
                <label className='ecg-label'>Tensión (kV)</label>
                <input type='text' className='ecg-input ecg-input-readonly' value={nodoResuelto.tension ?? '—'} readOnly />
              </div>
              <div className='ecg-form-field' style={{ maxWidth: 180 }}>
                <label className='ecg-label'>Latitud</label>
                <input type='text' className='ecg-input ecg-input-readonly' value={nodoResuelto.y != null ? Number(nodoResuelto.y).toFixed(6) : '—'} readOnly />
              </div>
              <div className='ecg-form-field' style={{ maxWidth: 180 }}>
                <label className='ecg-label'>Longitud</label>
                <input type='text' className='ecg-input ecg-input-readonly' value={nodoResuelto.x != null ? Number(nodoResuelto.x).toFixed(6) : '—'} readOnly />
              </div>
            </div>
          )}
        </div>
        <div className='ecg-form-row'>
          <div className='ecg-form-field' style={{ maxWidth: 280 }}>
            <label className='ecg-label'>Tipo de Generación Distribuida</label>
            <div className='ecg-radio-group'>
              <label className='ecg-radio-option'>
                <input
                  type='radio'
                  name='tipoGd'
                  value='PMGD'
                  checked={tipoGd === 'PMGD'}
                  onChange={() => setTipoGd('PMGD')}
                />
                PMGD
              </label>
              <label className='ecg-radio-option'>
                <input
                  type='radio'
                  name='tipoGd'
                  value='EG'
                  checked={tipoGd === 'EG'}
                  onChange={() => setTipoGd('EG')}
                />
                EG
              </label>
            </div>
          </div>
          <div className='ecg-form-field' style={{ maxWidth: 200 }}>
            <label className='ecg-label'>Potencia (kW)</label>
            <input
              type='number'
              className='ecg-input'
              placeholder={tipoGd === 'PMGD' ? '300 — 9000' : '1 — 300'}
              value={potenciaKw}
              onChange={(e) => setPotenciaKw(e.target.value)}
              min={tipoGd === 'PMGD' ? 300 : 1}
              max={tipoGd === 'PMGD' ? 9000 : 300}
            />
          </div>
          <div className='ecg-form-field' style={{ maxWidth: 200 }}>
            <label className='ecg-label'>Tecnología</label>
            <input type='text' className='ecg-input' value='Fotovoltaico (FV)' disabled />
          </div>
        </div>
        <div className='ecg-form-row'>
          <div className='ecg-form-field' style={{ maxWidth: 200 }}>
            <label className='ecg-label'>Naturaleza FP</label>
            <select
              className='ecg-input'
              value={fpNaturaleza}
              onChange={(e) => setFpNaturaleza(e.target.value as 'inductivo' | 'capacitivo')}>
              <option value='capacitivo'>Capacitivo (adelantado)</option>
              <option value='inductivo'>Inductivo (atrasado)</option>
            </select>
          </div>
          <div className='ecg-form-field' style={{ maxWidth: 300 }}>
            <label className='ecg-label'>
              Factor de Potencia: <strong>{factorPotencia.toFixed(2)}</strong>
              <span className='vdr-ecg-label-range'>
                ({fpBounds.min.toFixed(2)} – {fpBounds.max.toFixed(2)})
              </span>
            </label>
            <Slider
              value={factorPotencia}
              onChange={(_, v) =>
                setFactorPotencia(clampFpMagnitud(v as number, tipoGd, fpNaturaleza))
              }
              min={fpBounds.min}
              max={fpBounds.max}
              step={0.01}
              valueLabelDisplay='auto'
              sx={{ color: 'var(--vdr-cyan-dark)' }}
            />
            <p className='ecg-hint' style={{ marginTop: '0.25rem', fontSize: '12px' }}>
              {tipoGd === 'PMGD'
                ? 'PMGD (NTCO): inductivo ≥ 0,95; capacitivo ≥ 0,96 (Art. 3-19/3-31).'
                : 'EG (Netbilling): inductivo y capacitivo ≥ 0,95 (Art. 5-18/5-19).'}
            </p>
          </div>
          <div className='ecg-form-field' style={{ maxWidth: 250 }}>
            <label className='ecg-label'>Tipo de Inversor</label>
            <select
              className='ecg-input'
              value={tipoInversor}
              onChange={(e) => setTipoInversor(e.target.value)}>
              <option value='inversor'>Inversor</option>
              <option value='sincrono'>Síncrono</option>
              <option value='asincrono'>Asíncrono</option>
              <option value='asincrono_doble'>Asíncrono doble alimentado</option>
            </select>
          </div>
        </div>

        {/* Batería */}
        <div className='vdr-ecg-battery'>
          <CheckBox
            label='Mi proyecto incluye batería'
            checked={bateriaIncluida}
            onChange={(e) => setBateriaIncluida(e.target.checked)}
          />
          {bateriaIncluida && (
            <div className='ecg-battery-fields'>
              <div className='ecg-form-field' style={{ maxWidth: 180 }}>
                <label className='ecg-label'>Potencia batería (kW)</label>
                <input
                  type='number'
                  className='ecg-input'
                  value={batPotencia}
                  onChange={(e) => setBatPotencia(e.target.value)}
                  min={0}
                />
              </div>
              <div className='ecg-form-field' style={{ maxWidth: 180 }}>
                <label className='ecg-label'>Energía batería (kWh)</label>
                <input
                  type='number'
                  className='ecg-input'
                  value={batEnergia}
                  onChange={(e) => setBatEnergia(e.target.value)}
                  min={0}
                />
              </div>
              <div className='ecg-form-field' style={{ maxWidth: 180 }}>
                <label className='ecg-label'>Bloque carga (HH:MM-HH:MM)</label>
                <input
                  type='text'
                  className='ecg-input'
                  placeholder='22:00-06:00'
                  value={batBloqueCarga}
                  onChange={(e) => setBatBloqueCarga(e.target.value)}
                />
              </div>
              <div className='ecg-form-field' style={{ maxWidth: 180 }}>
                <label className='ecg-label'>Bloque descarga (HH:MM-HH:MM)</label>
                <input
                  type='text'
                  className='ecg-input'
                  placeholder='10:00-16:00'
                  value={batBloqueDescarga}
                  onChange={(e) => setBatBloqueDescarga(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Acciones */}
      <div className='ecg-actions'>
        <button
          className='primary-button-style vdr-ecg-cta'
          onClick={handleSubmit}
          disabled={submitting}
          style={{ minWidth: 240 }}>
          {submitting ? <CircularProgress size={20} sx={{ color: 'var(--vdr-ink)' }} /> : 'Ejecutar estudio base'}
        </button>
      </div>
    </div>
  );
};

export default PrivateEstudioConexionPage;
